import type { SubmitHandler } from "react-hook-form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AxiosError, HttpStatusCode } from "axios";
import { z } from "zod";

import { login } from "@/api";
import { AuthScreen } from "@/components/auth/auth-screen";
import { useAdminStore } from "@/stores";
import { Button, errorToast, Input, Message, PasswordInput } from "@/ui";

const schema = z.object({
  email: z.string().min(1, { message: "The email field is required." }),
  password: z.string().min(1, { message: "The password field is required." }),
});

type LoginFormData = z.infer<typeof schema>;

const LoginPage = () => {
  const navigate = useNavigate();
  const setToken = useAdminStore((s) => {
    return s.setToken;
  });
  const setAdmin = useAdminStore((s) => {
    return s.setAdmin;
  });
  const search = Route.useSearch();

  const {
    formState: { errors },
    handleSubmit,
    register,
    setError,
  } = useForm<LoginFormData>({
    resolver: zodResolver(schema),
  });

  const { mutate: loginMutation } = useMutation({
    mutationFn: login,
    onSuccess: (data) => {
      setToken(data.accessToken);
      setAdmin(data.admin);
      void navigate({ to: search.redirect ?? "/profiles" });
    },
    onError: (err) => {
      if (!(err instanceof AxiosError)) {
        errorToast(err);

        return;
      }

      const credentialsWereRejected = err.response?.status === HttpStatusCode.Unauthorized;
      const passwordExpiredAndNewLinkWasEmailed = err.response?.status === HttpStatusCode.Forbidden;

      if (credentialsWereRejected) {
        setError("root.invalidCredentials", { type: "401" });
      } else if (passwordExpiredAndNewLinkWasEmailed) {
        setError("root.passwordExpired", { type: "403" });
      } else {
        errorToast(err);
      }
    },
  });

  const onSubmit: SubmitHandler<LoginFormData> = (data) => {
    loginMutation(data);
  };

  return (
    <AuthScreen title="Welcome to EO Care">
      <form
        className="max-w-form flex flex-col items-start justify-center gap-4"
        onSubmit={handleSubmit(onSubmit)}
      >
        <h2 className="text-xl font-semibold text-content-strong">Login</h2>

        <Input
          containerClassName="w-full"
          error={errors.email?.message}
          id="email"
          label="Email"

          placeholder="Enter your email address"
          {...register("email")}
        />

        <PasswordInput
          containerClassName="mt-2 w-full"
          error={errors.password?.message}
          id="password"
          label="Password"
          placeholder="Enter your password"
          {...register("password")}
        />

        {errors.root?.invalidCredentials ? (
          <Message
            className="text-sm"
            error="These credentials don't seem to match any of our records"
          />
        ) : null}

        {errors.root?.passwordExpired ? (
          <Message
            className="text-sm"
            error="Your password has expired. We've emailed you a link to set a new one."
          />
        ) : null}

        <Button className="mt-6 w-full" size="md" type="submit" variant="primary">
          Login
        </Button>

        <Link
          className="rounded-sm text-sm text-accent-text underline underline-offset-2 hover:no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:outline-none"
          to="/forgot-password"
        >
          Forgot your password?
        </Link>
      </form>
    </AuthScreen>
  );
};

export const Route = createFileRoute("/_guest/login/")({ component: LoginPage });
